/**
 * <header-analytics> — GA4 header engagement tracking.
 *
 * Uses a single delegated click listener (plus a `toggle` capture listener for
 * dropdown/drawer opens) rather than instrumenting each snippet, so it survives
 * menu changes automatically.
 *
 * The theme has no direct access to gtag/dataLayer (GA4 runs in Shopify's Web
 * Pixel sandbox), so every interaction is published via Shopify.analytics.publish
 * under the custom event `ps_header:interaction`. A separate Custom Pixel
 * (pixels/header_tracking.js) subscribes to it and forwards to GA4 as a single
 * `header_interaction` event.
 *
 * See the plan/data model for the parameter set: interaction, element_type,
 * element_text, link_url, nav_menu, nav_level, nav_path, device.
 */
class HeaderAnalytics extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.measurementId = this.getAttribute('data-ga4-id') || '';
    this.debug = this.getAttribute('data-ga4-debug') === 'true';

    this._onClick = this.onClick.bind(this);
    this._onToggle = this.onToggle.bind(this);

    this.addEventListener('click', this._onClick);
    // `toggle` does not bubble, so listen in the capture phase.
    this.addEventListener('toggle', this._onToggle, true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('toggle', this._onToggle, true);
  }

  onClick(event) {
    const el = event.target.closest('a, button, summary');
    if (!el || !this.contains(el)) return;

    // A <summary> click toggles a <details>; that is captured as `menu_open`
    // via onToggle instead, to avoid double-firing on open.
    if (el.tagName === 'SUMMARY') return;

    const info = this.classify(el);
    if (!info) return;

    this.publish(Object.assign({ interaction: 'click' }, info));
  }

  onToggle(event) {
    const details = event.target;
    if (!details || details.tagName !== 'DETAILS' || !details.open) return;

    // The <summary> is the label/handle carrier for the dropdown being opened.
    const summary = details.querySelector(':scope > summary');
    if (!summary) return;

    const info = this.classify(summary);
    if (!info) return;

    this.publish(Object.assign({ interaction: 'menu_open' }, info));
  }

  /**
   * Derive the GA4 params for a clicked/opened element from existing markup.
   * Returns null for elements we don't want to track.
   */
  classify(el) {
    const id = el.id || '';
    const params = {
      element_type: null,
      element_text: this.textOf(el),
      link_url: el.tagName === 'A' ? this.normalizeUrl(el.getAttribute('href')) : null,
      nav_menu: this.navMenu(el),
      nav_level: null,
      nav_path: null,
      device: this.device(el),
    };

    // Menu items — matched by id prefix. Depth/path come from DOM nesting, NOT
    // from splitting the id: menu handles themselves contain hyphens
    // (e.g. "projector-screens"), so the id has no reliable segment count.
    if (/^Header(Menu|Drawer)-/.test(id)) {
      const path = this.navPathParts(el);
      params.element_type = 'nav_link';
      params.nav_level = String(path.length); // 1 = top level, 2/3 = submenu depth
      params.nav_path = path.join(' > ');
      return params;
    }

    // Utility navigation
    if (el.closest('#cart-icon-bubble') || el.id === 'cart-icon-bubble') {
      params.element_type = 'cart';
      params.nav_menu = 'utility';
      return params;
    }
    if (el.closest('.header__utility-navigation-icon--account, .menu-drawer__account')) {
      params.element_type = 'account';
      return params;
    }
    if (el.closest('.header__utility-navigation-icon--chat')) {
      params.element_type = 'chat';
      params.nav_menu = 'utility';
      return params;
    }

    // Logo
    if (el.closest('.header__logo')) {
      params.element_type = 'logo';
      params.nav_menu = 'utility';
      return params;
    }

    // Search
    if (el.closest('.header__icon--search')) {
      params.element_type = 'search_toggle';
      params.nav_menu = 'utility';
      return params;
    }
    if (el.closest('.search__button')) {
      params.element_type = 'search_submit';
      params.nav_menu = 'utility';
      return params;
    }

    // Help menu toggle (summary is handled by onToggle; this catches the button/back)
    if (el.closest('.header__help-menu-button, .header__help-menu-button-back')) {
      params.element_type = 'help_toggle';
      params.nav_menu = 'utility';
      return params;
    }

    // Drawer hamburger toggle
    if (el.closest('.header__icon--menu')) {
      params.element_type = 'drawer_toggle';
      params.nav_menu = 'drawer';
      return params;
    }

    // Social links
    if (el.closest('.list-social__link')) {
      params.element_type = 'social';
      return params;
    }

    // CTA blocks
    if (el.closest('.header__cta-block')) {
      params.element_type = 'cta';
      params.nav_path = this.navPathParts(el).join(' > ');
      return params;
    }

    // Localization selectors
    if (el.closest('localization-form')) {
      params.element_type = 'localization';
      return params;
    }

    // Any other tracked anchor inside the header (e.g. richtext links in help menu)
    if (el.tagName === 'A' && params.link_url) {
      params.element_type = 'nav_link';
      return params;
    }

    return null;
  }

  /** left / right / drawer / utility, from the nearest [data-nav-menu] ancestor. */
  navMenu(el) {
    const nav = el.closest('[data-nav-menu]');
    return nav ? nav.getAttribute('data-nav-menu') : 'utility';
  }

  /** mobile if inside the drawer, else desktop; fallback to viewport. */
  device(el) {
    if (el.closest('header-drawer')) return 'mobile';
    if (el.closest('.primary-nav__inline-menu')) return 'desktop';
    try {
      if (window.matchMedia) {
        return window.matchMedia('(max-width: 989px)').matches ? 'mobile' : 'desktop';
      }
    } catch (e) {
      /* ignore */
    }
    return 'desktop';
  }

  /**
   * Build a breadcrumb like ["Projector Screens", "Fixed Frame"] for a menu item.
   * Walks the parent submenu titles that Dawn renders (.primary-nav__submenu-title
   * / .menu-drawer__close-button-title), then appends the item's own label. The
   * returned length is also used as nav_level (true DOM depth).
   */
  navPathParts(el) {
    const parts = [];
    let node = el.parentElement;
    while (node && this.contains(node)) {
      const title = node.querySelector(
        ':scope > .primary-nav__submenu-title, :scope > * > .menu-drawer__close-button-title'
      );
      if (title) {
        const t = title.textContent.trim();
        if (t && parts.indexOf(t) === -1) parts.unshift(t);
      }
      node = node.parentElement;
    }
    parts.push(this.textOf(el));
    // De-dupe consecutive repeats (a leaf whose parent title equals its own text).
    return parts.filter((p, i) => p && p !== parts[i - 1]);
  }

  /** Visible label of an element, trimmed and capped. */
  textOf(el) {
    const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    return text.slice(0, 100);
  }

  /** Strip origin so identical destinations compare equal across menus. */
  normalizeUrl(href) {
    if (!href) return null;
    try {
      return new URL(href, window.location.origin).pathname;
    } catch (e) {
      return href;
    }
  }

  publish(params) {
    params.measurement_id = this.measurementId;
    // Passed through to the pixel, which does the per-event console logging.
    params.debug = this.debug;

    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('ps_header:interaction', params);
      } else if (this.debug) {
        // eslint-disable-next-line no-console
        console.warn('[header_interaction] Shopify.analytics.publish unavailable — event not sent to pixel.');
      }
    } catch (e) {
      if (this.debug) {
        // eslint-disable-next-line no-console
        console.warn('[header_interaction] publish failed', e);
      }
    }
  }
}

customElements.define('header-analytics', HeaderAnalytics);
